/**
 * 人事履歷 5 分頁檢視（基本資料 / 學歷 / 經歷 / 證照 / 變更歷史）。
 * admin 與 self 共用：mode='self' 時「僅行政人員可改」欄位唯讀並提示洽行政人員。
 * 修改一律開彈出視窗（D1·規格 §6）。訊息走 useMessage（禁 alert / 裸 Snackbar）。
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Divider, Grid, IconButton, Link, MenuItem, Paper, Stack, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';

import { useMessage } from '../../contexts/MessageContext';
import {
  Certification, Education, Experience, HistoryItem, HrProfile, HrProfileUpdate, ResumeSection,
} from '../../types/eeHrResume';

export interface ResumeTabsHandlers {
  saveProfile: (u: HrProfileUpdate) => Promise<HrProfile>;
  reveal?: () => Promise<{ id_number: string }>;
  loadHistory: (offset: number, limit: number) => Promise<{ total: number; items: HistoryItem[] }>;
  createDetail: (section: ResumeSection, data: any) => Promise<any>;
  updateDetail: (section: ResumeSection, id: number, data: any) => Promise<any>;
  deleteDetail: (section: ResumeSection, id: number) => Promise<void>;
}

interface Props {
  profile: HrProfile;
  mode: 'admin' | 'self';
  onReload: () => void;
  handlers: ResumeTabsHandlers;
}



const ResumeTabs: React.FC<Props> = ({ profile, mode, onReload, handlers }) => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { showSuccess, showApiError } = useMessage();
  const tt = (k: string, d: string) => t(`hrResume.${k}`, d);
  const isAdmin = mode === 'admin';

  const genderLabel = (g?: string | null) =>
    g === 'M' ? tt('genderM', '男') : g === 'F' ? tt('genderF', '女') : (g || '');
  const DEGREES = ['doctor', 'master', 'bachelor', 'associate', 'senior_high', 'other'];
  const degreeLabel = (d?: string | null) => d ? tt(`degree_${d}`, {
    doctor: '博士', master: '碩士', bachelor: '學士', associate: '專科',
    senior_high: '高中職', other: '其他',
  }[d] || d) : '';
  const STATUSES = ['graduated', 'dropped', 'studying'];
  const statusLabel = (s?: string | null) => s ? tt(`eduStatus_${s}`, {
    graduated: '畢業', dropped: '肄業', studying: '就學中',
  }[s] || s) : '';

  // ── 變更歷史：欄位名 / 資料類別 / 動作 的中文標籤（R4）──
  const FIELD_LABELS: Record<string, string> = {
    id_number: '身分證／居留證', name_en: '英文姓名', gender: '性別', birthday: '生日',
    address_registered: '戶籍地址', address_mailing: '通訊地址', phone_home: '住家電話',
    emergency_name: '緊急聯絡人', emergency_relation: '緊急聯絡人關係', emergency_phone: '緊急聯絡人電話',
    remark: '備註', school: '學校', major: '科系', degree: '學位', start_ym: '起始年月',
    end_ym: '結束年月', status: '狀態', sort_order: '排序', company: '公司', title: '職稱',
    description: '工作內容', name: '證照名稱', issuer: '發證單位', cert_no: '證號',
    issued_date: '發證日', expire_date: '到期日',
  };
  const fieldLabel = (f: string) => tt(`field_${f}`, FIELD_LABELS[f] || f);
  const targetLabel = (tg: string) => tt(`target_${tg}`, {
    profile: '基本資料', education: '學歷', experience: '經歷', certification: '證照',
  }[tg] || tg);
  const actionLabel = (a: string) => tt(`action_${a}`, {
    create: '新增', update: '修改', delete: '刪除', reveal_id: '顯示身分證',
  }[a] || a);
  // 依欄位把值顯示成人看得懂的文字；空值標示為「（空）」
  const fmtVal = (f: string, v: any): string => {
    if (v === null || v === undefined || v === '') return tt('histEmpty', '（空）');
    if (f === 'gender') return genderLabel(String(v));
    if (f === 'degree') return degreeLabel(String(v));
    if (f === 'status') return statusLabel(String(v));
    return String(v);
  };

  const [tab, setTab] = useState(0);

  // ── 基本資料編輯彈窗 ──
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState<HrProfileUpdate>({});
  const [revealed, setRevealed] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const openEdit = () => {
    setForm({
      id_number: '',
      name_en: profile.name_en || '',
      gender: profile.gender || '',
      birthday: profile.birthday || '',
      address_registered: profile.address_registered || '',
      remark: profile.remark || '',
      address_mailing: profile.address_mailing || '',
      phone_home: profile.phone_home || '',
      emergency_name: profile.emergency_name || '',
      emergency_relation: profile.emergency_relation || '',
      emergency_phone: profile.emergency_phone || '',
    });
    setEditOpen(true);
  };

  const submitProfile = async () => {
    setSaving(true);
    try {
      // 2026-10-30：除帳號資料與備註外，個人資料本人與行政人員都可送；id_number 空字串代表不變更
      const payload: HrProfileUpdate = {
        name_en: form.name_en,
        gender: form.gender,
        birthday: form.birthday || null,
        address_registered: form.address_registered,
        address_mailing: form.address_mailing,
        phone_home: form.phone_home,
        emergency_name: form.emergency_name,
        emergency_relation: form.emergency_relation,
        emergency_phone: form.emergency_phone,
      };
      if (form.id_number && form.id_number.trim()) payload.id_number = form.id_number.trim();
      if (isAdmin) payload.remark = form.remark;   // 備註僅行政人員可改
      await handlers.saveProfile(payload);
      showSuccess('SYS220001', { name: profile.account.username });
      setEditOpen(false);
      onReload();
    } catch (err) { showApiError(err); }
    finally { setSaving(false); }
  };

  const doReveal = async () => {
    if (!handlers.reveal) return;
    try {
      const r = await handlers.reveal();
      setRevealed(r.id_number);
    } catch (err) { showApiError(err); }
  };

  const setF = (k: keyof HrProfileUpdate) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [k]: e.target.value }));

  // ── 明細彈窗 ──
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSection, setDetailSection] = useState<ResumeSection>('educations');
  const [detailForm, setDetailForm] = useState<any>({});
  const [detailId, setDetailId] = useState<number | null>(null);

  const openDetail = (section: ResumeSection, row?: any) => {
    setDetailSection(section);
    setDetailId(row ? row.id : null);
    setDetailForm(row ? { ...row } : {});
    setDetailOpen(true);
  };
  const setD = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDetailForm((prev: any) => ({ ...prev, [k]: e.target.value }));

  const submitDetail = async () => {
    setSaving(true);
    try {
      if (detailId) await handlers.updateDetail(detailSection, detailId, detailForm);
      else await handlers.createDetail(detailSection, detailForm);
      showSuccess('SYS220001', { name: profile.account.username });
      setDetailOpen(false);
      onReload();
    } catch (err) { showApiError(err); }
    finally { setSaving(false); }
  };

  // 刪除確認改用 MUI Dialog（S6；不再用 window.confirm）
  const [delTarget, setDelTarget] = useState<{ section: ResumeSection; id: number } | null>(null);
  const removeDetail = (section: ResumeSection, id: number) => setDelTarget({ section, id });
  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await handlers.deleteDetail(delTarget.section, delTarget.id);
      setDelTarget(null);
      onReload();
    } catch (err) { showApiError(err); }
  };

  // ── 變更歷史 ──
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [histTotal, setHistTotal] = useState(0);
  const loadHist = useCallback(async () => {
    try {
      const r = await handlers.loadHistory(0, 50);
      setHistory(r.items);
      setHistTotal(r.total);
    } catch (err) { showApiError(err); }
  }, [handlers, showApiError]);
  useEffect(() => { if (tab === 4) loadHist(); }, [tab, loadHist]);

  const ro = { readOnly: true };

  // ── render ──
  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ mb: 2 }}>
        <Tab label={tt('tabBasic', '基本資料')} />
        <Tab label={tt('tabEdu', '學歷')} />
        <Tab label={tt('tabExp', '經歷')} />
        <Tab label={tt('tabCert', '證照')} />
        <Tab label={tt('tabHistory', '變更歷史')} />
      </Tabs>

      {/* 基本資料 */}
      {tab === 0 && (
        <Stack spacing={2}>
          {/* 帳號資料：框線 + 底色標出，和可修改的個人資料明顯區分（porsche 2026-10-30·深淺主題皆清楚）*/}
          <Paper variant="outlined"
            sx={{ p: 2, bgcolor: 'action.hover', borderColor: 'divider', borderWidth: 2 }}>
            <Typography variant="subtitle2" gutterBottom>{tt('account', '帳號資料')}</Typography>
            <Grid container spacing={1}>
              <Grid size={{ xs: 6, sm: 3 }}><Field label={tt('name', '姓名')} value={profile.account.username} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><Field label={tt('accountCode', '帳號')} value={profile.account.account} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><Field label="Email" value={profile.account.email || ''} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><PhoneField label={tt('phone', '手機')} value={profile.account.phone || ''} /></Grid>
            </Grid>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">
                {tt('accountReadonlyNote', '帳號資料不在此修改')}
              </Typography>
              <Link component="button" variant="body2"
                onClick={() => nav(mode === 'self' ? '/my_profile' : '/tenant_users')}>
                {mode === 'self' ? tt('editAccount', '到我的個人資料修改') : tt('editAccountAdmin', '到組織使用者管理修改')}
              </Link>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">{tt('personal', '個人資料')}</Typography>
              <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={openEdit}>
                {tt('edit', '修改')}
              </Button>
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Field label={tt('idNumber', '身分證 / 居留證')}
                    value={profile.id_number_masked || tt('notSet', '未填')} />
                  {profile.id_number_last4 && handlers.reveal && (
                    <IconButton size="small" onClick={doReveal} title={tt('reveal', '顯示完整')}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
                {revealed && <Typography variant="caption" color="error">{revealed}</Typography>}
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}><Field label={tt('nameEn', '英文姓名')} value={profile.name_en || ''} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><Field label={tt('gender', '性別')} value={genderLabel(profile.gender)} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><Field label={tt('birthday', '生日')} value={profile.birthday || ''} /></Grid>
              <Grid size={{ xs: 12, sm: 9 }}><Field label={tt('addrReg', '戶籍地址')} value={profile.address_registered || ''} /></Grid>
              <Grid size={{ xs: 12, sm: 9 }}><Field label={tt('addrMail', '通訊地址')} value={profile.address_mailing || ''} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><PhoneField label={tt('phoneHome', '住家電話')} value={profile.phone_home || ''} /></Grid>
              <Grid size={{ xs: 6, sm: 4 }}><Field label={tt('emgName', '緊急聯絡人')} value={profile.emergency_name || ''} /></Grid>
              <Grid size={{ xs: 6, sm: 4 }}><Field label={tt('emgRel', '關係')} value={profile.emergency_relation || ''} /></Grid>
              <Grid size={{ xs: 6, sm: 4 }}><PhoneField label={tt('emgPhone', '聯絡電話')} value={profile.emergency_phone || ''} /></Grid>
              {isAdmin && <Grid size={{ xs: 12 }}><Field label={tt('remark', '備註')} value={profile.remark || ''} /></Grid>}
            </Grid>
          </Paper>
        </Stack>
      )}

      {/* 學歷 */}
      {tab === 1 && (
        <DetailList title={tt('tabEdu', '學歷')} onAdd={() => openDetail('educations')}
          rows={profile.educations}
          cols={[tt('school', '學校'), tt('major', '科系'), tt('degreeCol', '學位'), tt('period', '期間'), tt('statusCol', '狀態')]}
          render={(r: Education) => [r.school, r.major, degreeLabel(r.degree),
            `${r.start_ym || ''} ~ ${r.end_ym || ''}`, statusLabel(r.status)]}
          onEdit={(r) => openDetail('educations', r)} onDel={(r) => removeDetail('educations', r.id)} />
      )}

      {/* 經歷 */}
      {tab === 2 && (
        <DetailList title={tt('tabExp', '經歷')} onAdd={() => openDetail('experiences')}
          rows={profile.experiences}
          cols={[tt('company', '公司'), tt('title', '職稱'), tt('period', '期間'), tt('jobDesc', '工作內容')]}
          render={(r: Experience) => [r.company, r.title, `${r.start_ym || ''} ~ ${r.end_ym || ''}`, r.description]}
          onEdit={(r) => openDetail('experiences', r)} onDel={(r) => removeDetail('experiences', r.id)} />
      )}

      {/* 證照 */}
      {tab === 3 && (
        <DetailList title={tt('tabCert', '證照')} onAdd={() => openDetail('certifications')}
          rows={profile.certifications}
          cols={[tt('certName', '名稱'), tt('issuer', '發證單位'), tt('certNo', '證號'), tt('issued', '發證日'), tt('expire', '到期日')]}
          render={(r: Certification) => [r.name, r.issuer, r.cert_no, r.issued_date, r.expire_date]}
          onEdit={(r) => openDetail('certifications', r)} onDel={(r) => removeDetail('certifications', r.id)} />
      )}

      {/* 變更歷史（R4：中文欄位名、target·action 中文、可展開看改前改後）*/}
      {tab === 4 && (
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">{tt('histTotal', '共 {n} 筆').replace('{n}', String(histTotal))}</Typography>
          {history.map(h => {
            const keys = Object.keys(h.changed_fields || {});
            const summary = keys.map(fieldLabel).join('、');
            return (
              <Accordion key={h.id} variant="outlined" disableGutters
                sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={keys.length > 0 ? <ExpandMoreIcon /> : null}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ width: '100%' }}>
                    <Typography variant="body2">{new Date(h.changed_at).toLocaleString()}</Typography>
                    <Chip size="small" label={h.channel === 'self' ? tt('bySelf', '本人') : tt('byAdmin', '行政人員')} />
                    <Typography variant="body2">{h.changed_by_name}</Typography>
                    <Chip size="small" variant="outlined" color="primary"
                      label={`${targetLabel(h.target)} · ${actionLabel(h.action)}`} />
                    {summary && (
                      <Typography variant="caption" color="text.secondary">{summary}</Typography>
                    )}
                  </Stack>
                </AccordionSummary>
                {keys.length > 0 && (
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{tt('histField', '欄位')}</TableCell>
                          <TableCell>{tt('histOld', '修改前')}</TableCell>
                          <TableCell>{tt('histNew', '修改後')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {keys.map(k => {
                          const cf: any = (h.changed_fields as any)[k] || {};
                          if ('revealed' in cf) {
                            return (
                              <TableRow key={k}>
                                <TableCell>{fieldLabel(k)}</TableCell>
                                <TableCell colSpan={2}>
                                  {tt('histRevealed', '已顯示完整號碼')}（{cf.revealed}）
                                </TableCell>
                              </TableRow>
                            );
                          }
                          return (
                            <TableRow key={k}>
                              <TableCell>{fieldLabel(k)}</TableCell>
                              <TableCell>{fmtVal(k, cf.old)}</TableCell>
                              <TableCell>{fmtVal(k, cf.new)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                )}
              </Accordion>
            );
          })}
          {history.length === 0 && <Typography variant="body2" color="text.secondary">{tt('noHistory', '尚無變更紀錄')}</Typography>}
        </Stack>
      )}

      {/* 基本資料編輯彈窗 */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{tt('editPersonal', '修改個人資料')}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {/* 2026-10-30：身分證 / 英文姓名 / 性別 / 生日 / 戶籍地址，本人與行政人員都可改 */}
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" label={tt('idNumberNew', '身分證 / 居留證（留空不變更）')}
                value={form.id_number || ''} onChange={setF('id_number')} />
            </Grid>
            <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('nameEn', '英文姓名')} value={form.name_en || ''} onChange={setF('name_en')} /></Grid>
            <Grid size={{ xs: 6 }}>
              <TextField select fullWidth size="small" label={tt('gender', '性別')} value={form.gender || ''} onChange={setF('gender')}>
                <MenuItem value="">—</MenuItem>
                <MenuItem value="M">{tt('genderM', '男')}</MenuItem>
                <MenuItem value="F">{tt('genderF', '女')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6 }}><TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} label={tt('birthday', '生日')} value={form.birthday || ''} onChange={setF('birthday')} /></Grid>
            <Grid size={{ xs: 12 }}><TextField fullWidth size="small" label={tt('addrReg', '戶籍地址')} value={form.address_registered || ''} onChange={setF('address_registered')} /></Grid>
            <Grid size={{ xs: 12 }}><TextField fullWidth size="small" label={tt('addrMail', '通訊地址')} value={form.address_mailing || ''} onChange={setF('address_mailing')} /></Grid>
            <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('phoneHome', '住家電話')} value={form.phone_home || ''} onChange={setF('phone_home')} /></Grid>
            <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('emgName', '緊急聯絡人')} value={form.emergency_name || ''} onChange={setF('emergency_name')} /></Grid>
            <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('emgRel', '關係')} value={form.emergency_relation || ''} onChange={setF('emergency_relation')} /></Grid>
            <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('emgPhone', '聯絡電話')} value={form.emergency_phone || ''} onChange={setF('emergency_phone')} /></Grid>
            {isAdmin && <Grid size={{ xs: 12 }}><TextField fullWidth size="small" multiline minRows={2} label={tt('remark', '備註')} value={form.remark || ''} onChange={setF('remark')} /></Grid>}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{tt('cancel', '取消')}</Button>
          <Button variant="contained" onClick={submitProfile} disabled={saving}>{tt('save', '儲存')}</Button>
        </DialogActions>
      </Dialog>

      {/* 明細編輯彈窗 */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{detailId ? tt('editItem', '修改') : tt('addItem', '新增')}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {detailSection === 'educations' && (
              <>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('school', '學校')} value={detailForm.school || ''} onChange={setD('school')} /></Grid>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('major', '科系')} value={detailForm.major || ''} onChange={setD('major')} /></Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField select fullWidth size="small" label={tt('degreeCol', '學位')} value={detailForm.degree || ''} onChange={setD('degree')}>
                    <MenuItem value="">—</MenuItem>
                    {DEGREES.map(d => <MenuItem key={d} value={d}>{degreeLabel(d)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField select fullWidth size="small" label={tt('statusCol', '狀態')} value={detailForm.status || ''} onChange={setD('status')}>
                    <MenuItem value="">—</MenuItem>
                    {STATUSES.map(s => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('startYm', '起（YYYY-MM）')} value={detailForm.start_ym || ''} onChange={setD('start_ym')} /></Grid>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('endYm', '訖（YYYY-MM）')} value={detailForm.end_ym || ''} onChange={setD('end_ym')} /></Grid>
              </>
            )}
            {detailSection === 'experiences' && (
              <>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('company', '公司')} value={detailForm.company || ''} onChange={setD('company')} /></Grid>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('title', '職稱')} value={detailForm.title || ''} onChange={setD('title')} /></Grid>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('startYm', '起（YYYY-MM）')} value={detailForm.start_ym || ''} onChange={setD('start_ym')} /></Grid>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('endYm', '訖（YYYY-MM）')} value={detailForm.end_ym || ''} onChange={setD('end_ym')} /></Grid>
                <Grid size={{ xs: 12 }}><TextField fullWidth size="small" multiline minRows={2} label={tt('jobDesc', '工作內容')} value={detailForm.description || ''} onChange={setD('description')} /></Grid>
              </>
            )}
            {detailSection === 'certifications' && (
              <>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('certName', '名稱')} value={detailForm.name || ''} onChange={setD('name')} /></Grid>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('issuer', '發證單位')} value={detailForm.issuer || ''} onChange={setD('issuer')} /></Grid>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label={tt('certNo', '證號')} value={detailForm.cert_no || ''} onChange={setD('cert_no')} /></Grid>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} label={tt('issued', '發證日')} value={detailForm.issued_date || ''} onChange={setD('issued_date')} /></Grid>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} label={tt('expire', '到期日')} value={detailForm.expire_date || ''} onChange={setD('expire_date')} /></Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>{tt('cancel', '取消')}</Button>
          <Button variant="contained" onClick={submitDetail} disabled={saving}>{tt('save', '儲存')}</Button>
        </DialogActions>
      </Dialog>

      {/* 刪除確認彈窗（S6）*/}
      <Dialog open={!!delTarget} onClose={() => setDelTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{tt('confirmDeleteTitle', '刪除確認')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{tt('confirmDelete', '確定刪除這一筆？刪除後無法復原。')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelTarget(null)}>{tt('cancel', '取消')}</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>{tt('delete', '刪除')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // 只讀顯示欄
  function Field({ label, value }: { label: string; value: string }) {
    return (
      <TextField fullWidth size="small" variant="standard" label={label}
        value={value} InputProps={ro} />
    );
  }

  // 電話顯示欄：可點撥號（tel:·去除空白與分隔符）。無值則退回一般唯讀欄。
  function PhoneField({ label, value }: { label: string; value: string }) {
    const tel = String(value || '').replace(/[^\d+]/g, '');
    if (!value || !tel) return <Field label={label} value={value} />;
    return (
      <Box sx={{ pt: '3px' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Link href={`tel:${tel}`} underline="hover" variant="body2">{value}</Link>
      </Box>
    );
  }

  // 明細清單（含新增 / 編輯 / 刪除）
  function DetailList<T extends { id: number }>({ title, cols, rows, render, onAdd, onEdit, onDel }: {
    title: string; cols: string[]; rows: T[]; render: (r: T) => (string | null | undefined)[];
    onAdd: () => void; onEdit: (r: T) => void; onDel: (r: T) => void;
  }) {
    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">{title}</Typography>
          <Button size="small" variant="outlined" onClick={onAdd}>{tt('add', '新增')}</Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>{cols.map(c => <TableCell key={c}>{c}</TableCell>)}<TableCell align="right" /></TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id}>
                {render(r).map((v, i) => <TableCell key={i}>{v || ''}</TableCell>)}
                <TableCell align="right">
                  <IconButton size="small" onClick={() => onEdit(r)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => onDel(r)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={cols.length + 1}>
              <Typography variant="body2" color="text.secondary">{tt('noData', '尚無資料')}</Typography>
            </TableCell></TableRow>}
          </TableBody>
        </Table>
      </Box>
    );
  }
};

export default ResumeTabs;
